import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  addLayoutItem,
  appendFieldToLayout,
  findGroup,
  materializeFormLayout,
  moveLayoutItem,
  newLayoutItemId,
  removeGroupFromLayout,
  removeHeadingFromLayout,
  removeDividerFromLayout,
  removeFieldFromLayout,
  layoutHasDiscountCode,
  removeDiscountCodeFromLayout,
  DISCOUNT_CODE_ITEM_ID,
  duplicateLayoutBlock,
  resolveFormLayout,
  ungroupInLayout,
  updateGroupInLayout,
  updateHeadingInLayout,
  setBlockDisabledInLayout,
  wrapFieldInGroup,
} from '@repo/lib';
import usePage from '@dashboard/hooks/usePage';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import { pageKey } from '@dashboard/lib/queryKeys';
import { toast } from '@dashboard/context/ToastContext';
import { confirm } from '@dashboard/lib/confirm';
import { fieldLabelText } from '@repo/lib';
import { useFields } from './FieldsContext';

const FormLayoutContext = createContext(null);

const errorMessage = error => error?.message || error?.error || 'Something went wrong. Try again.';

export function groupDisplayName(layout, groupId) {
  const groups = layout.filter(item => item.type === 'group');
  const index = groups.findIndex(group => group.id === groupId);
  const group = groups[index];
  if (!group) return 'Group';
  if (group.name) return group.name;
  return groups.filter(g => !g.name).length > 1 ? `Group ${index + 1}` : 'Group';
}

/**
 * `layoutKey` picks another layout on the page (e.g. the guest questions'). Only the page form has
 * the discount code box and field and group copies.
 */
export function FormLayoutProvider({
  children,
  layoutKey = 'formLayout',
  saveLayoutRequest = null,
  pageForm = true,
}) {
  const { page, updatePage } = usePage();
  const queryClient = useQueryClient();
  const queries = usePageQueries();
  const { state: fields, dispatch } = useFields();
  // Unsaved new fields opened from a group's "+ Add to group", by their draft key.
  const [draftGroups, setDraftGroups] = useState({});

  const savedLayout = page?.[layoutKey];
  const savedFields = useMemo(() => fields.filter(field => field._id), [fields]);
  const showsDiscountCode = pageForm && page?.type !== 'form' && !!page?.showCouponCodeField;
  // Switched off but still placed, the box keeps its row, disabled, so Enable brings it back there.
  const discountCodeDisabled =
    pageForm && page?.type !== 'form' && !showsDiscountCode && layoutHasDiscountCode(savedLayout);
  const hasDiscountCode = showsDiscountCode || discountCodeDisabled;
  // The box keeps its old place, under the fields, until it is moved; deleted, it has no row.
  const layout = useMemo(() => {
    const saved = materializeFormLayout(savedLayout, savedFields);
    if (!hasDiscountCode) return removeDiscountCodeFromLayout(saved);
    return layoutHasDiscountCode(saved)
      ? saved
      : [...saved, { type: 'discount-code', id: DISCOUNT_CODE_ITEM_ID }];
  }, [savedLayout, savedFields, hasDiscountCode]);

  const fieldsById = useMemo(
    () => new Map(fields.map(field => [String(field._id ?? field.key), field])),
    [fields],
  );

  const displayLayout = useMemo(() => {
    let next = layout;
    for (const field of fields) {
      if (field._id || !field.key) continue;
      const groupId = draftGroups[field.key];
      next = appendFieldToLayout(
        next,
        field.key,
        groupId && findGroup(next, groupId) ? groupId : null,
      );
    }
    return next;
  }, [layout, fields, draftGroups]);

  const toItems = useCallback(
    source =>
      resolveFormLayout(
        source,
        fields.map(field => ({ _id: String(field._id ?? field.key), order: field.order })),
      ).map(function attach(item) {
        if (item.type === 'field') return { ...item, field: fieldsById.get(item.fieldId) };
        if (item.type === 'group') return { ...item, items: item.items.map(attach) };
        return item;
      }),
    [fields, fieldsById],
  );

  const items = useMemo(() => toItems(displayLayout), [toItems, displayLayout]);

  const setCachedLayout = useCallback(
    next =>
      queryClient.setQueryData(pageKey(page._id), old =>
        old ? { ...old, [layoutKey]: next } : old,
      ),
    [queryClient, page?._id, layoutKey],
  );
  const save = useCallback(
    (pageId, next) =>
      saveLayoutRequest
        ? saveLayoutRequest(next)
        : queries.saveFormLayout(pageId, next).then(saved => saved.formLayout),
    [saveLayoutRequest, queries],
  );

  // Saves go one at a time, in order: two quick edits sent together could land on the server
  // the other way round and lose the second. Only the newest save may write the cache back.
  const saveQueue = useRef(Promise.resolve());
  const latestSave = useRef(0);

  // Until the seller places the box it stays out of saves, so other edits leave it under the fields.
  const applyLayout = useCallback(
    async (requested, { placesDiscountCode = false } = {}) => {
      const previous = savedLayout;
      const next =
        placesDiscountCode || layoutHasDiscountCode(previous)
          ? requested
          : removeDiscountCodeFromLayout(requested);
      const id = ++latestSave.current;
      queryClient.cancelQueries({ queryKey: pageKey(page._id) });
      setCachedLayout(next);
      const request = saveQueue.current.then(() => save(page._id, next));
      saveQueue.current = request.catch(() => {});
      try {
        const saved = await request;
        // A refetch while a newer save is queued could bring back a layout without it.
        if (id === latestSave.current) {
          setCachedLayout(saved);
          queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
        }
        return true;
      } catch (error) {
        if (id === latestSave.current) {
          setCachedLayout(previous);
          queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
        }
        toast({
          title: "Couldn't save the form layout",
          description: errorMessage(error),
          type: 'error',
        });
        return false;
      }
    },
    [page?._id, savedLayout, save, queryClient, setCachedLayout],
  );

  const savedIds = useMemo(
    () => new Set(savedFields.map(field => String(field._id))),
    [savedFields],
  );

  const actions = useMemo(
    () => ({
      // Drafts are shown in the layout while open but are saved by their own create call.
      saveLayout: next =>
        applyLayout(
          next
            .filter(item => item.type !== 'field' || savedIds.has(item.fieldId))
            .map(item =>
              item.type === 'group'
                ? {
                    ...item,
                    items: item.items.filter(
                      child => child.type !== 'field' || savedIds.has(child.fieldId),
                    ),
                  }
                : item,
            ),
          { placesDiscountCode: next.at(-1)?.type !== 'discount-code' },
        ),
      moveItem: (key, target) =>
        applyLayout(moveLayoutItem(layout, key, target), {
          placesDiscountCode: key === `discount-code:${DISCOUNT_CODE_ITEM_ID}`,
        }),
      groupField: async fieldId => {
        const ok = await applyLayout(
          wrapFieldInGroup(layout, String(fieldId), { id: newLayoutItemId(), columns: 2 }),
        );
        if (ok) toast({ title: 'Group added', type: 'success' });
        return ok;
      },
      setColumns: (groupId, columns) =>
        applyLayout(updateGroupInLayout(layout, groupId, { columns })),
      renameGroup: (groupId, name) => applyLayout(updateGroupInLayout(layout, groupId, { name })),
      ungroup: groupId => applyLayout(ungroupInLayout(layout, groupId)),
      addGroup: (columns = 2) => {
        const id = newLayoutItemId();
        return applyLayout(addLayoutItem(layout, { type: 'group', id, columns, items: [] })).then(
          ok => (ok ? id : null),
        );
      },
      addHeading: (
        level = 'heading',
        groupId = null,
        text = level === 'subheading' ? 'Subheading' : 'Heading',
      ) => {
        const id = newLayoutItemId();
        return applyLayout(
          addLayoutItem(layout, { type: 'heading', id, text, level }, groupId),
        ).then(ok => (ok ? id : null));
      },
      duplicateBlock: async key => {
        const duplicated = duplicateLayoutBlock(layout, key);
        if (!duplicated || !(await applyLayout(duplicated.layout))) return null;
        return duplicated.id;
      },
      updateHeading: (headingId, changes) =>
        applyLayout(updateHeadingInLayout(layout, headingId, changes)),
      removeHeading: async headingId => {
        const ok = await confirm('Delete this heading?', { confirm: 'Delete' });
        if (!ok) return false;
        return applyLayout(removeHeadingFromLayout(layout, headingId));
      },
      addDivider: (groupId = null) =>
        applyLayout(addLayoutItem(layout, { type: 'divider', id: newLayoutItemId() }, groupId)),
      removeDivider: dividerId => applyLayout(removeDividerFromLayout(layout, dividerId)),
      setBlockDisabled: (key, disabled) =>
        applyLayout(setBlockDisabledInLayout(layout, key, disabled)),
      showsDiscountCode,
      hasDiscountCode,
      discountCodeDisabled,
      setDiscountCodeDisabled: async disabled => {
        // Placed first, so a box still sitting at its default spot keeps its row once switched off.
        if (disabled && !(await applyLayout(layout, { placesDiscountCode: true }))) return false;
        const { error } = await updatePage.mutateAsync({ showCouponCodeField: !disabled });
        return !error;
      },
      addDiscountCode: async (groupId = null) => {
        const placed = addLayoutItem(
          removeDiscountCodeFromLayout(layout),
          { type: 'discount-code', id: DISCOUNT_CODE_ITEM_ID },
          groupId,
        );
        // Layout first: while the setting is off the placed box stays hidden, so it never shows at the end first.
        if (!(await applyLayout(placed, { placesDiscountCode: true }))) return false;
        const { error } = await updatePage.mutateAsync({ showCouponCodeField: true });
        return !error;
      },
      removeDiscountCode: async () => {
        const { error } = await updatePage.mutateAsync({ showCouponCodeField: false });
        if (error) return false;
        return applyLayout(removeDiscountCodeFromLayout(layout));
      },
      // The server already put the field in its group; the cached layout must agree before the
      // draft turns into a saved field, or it shows outside the group until the refetch lands.
      placeSavedField: (fieldId, groupId) =>
        queryClient.setQueryData(pageKey(page._id), old => {
          if (!old) return old;
          const id = String(fieldId);
          const base = removeFieldFromLayout(
            materializeFormLayout(old[layoutKey], savedFields),
            id,
          );
          return { ...old, [layoutKey]: appendFieldToLayout(base, id, groupId) };
        }),
      setDraftGroup: (draftKey, groupId) =>
        setDraftGroups(current => ({ ...current, [draftKey]: groupId })),
      draftGroupFor: draftKey => draftGroups[draftKey] ?? null,
      duplicateField: !pageForm
        ? null
        : async fieldId => {
            try {
              const { field, formLayout } = await queries.duplicateField(page._id, fieldId);
              dispatch({ type: 'append', values: [field] });
              setCachedLayout(formLayout);
              queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
              toast({
                title: 'Field duplicated',
                description: fieldLabelText(field.label),
                type: 'success',
              });
              return field;
            } catch (error) {
              toast({
                title: "Couldn't duplicate the field",
                description: errorMessage(error),
                type: 'error',
              });
              return null;
            }
          },
      duplicateGroup: !pageForm
        ? null
        : async groupId => {
            try {
              const result = await queries.duplicateGroup(page._id, groupId);
              dispatch({ type: 'append', values: result.fields });
              setCachedLayout(result.formLayout);
              queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
              toast({ title: 'Group duplicated', type: 'success' });
              return result.groupId;
            } catch (error) {
              toast({
                title: "Couldn't duplicate the group",
                description: errorMessage(error),
                type: 'error',
              });
              return null;
            }
          },
      // Deleting never takes fields with it: a group is emptied (moved out or ungrouped) first.
      deleteGroup: groupId => {
        const group = findGroup(layout, groupId);
        if (!group || group.items.length > 0) return Promise.resolve(false);
        return applyLayout(removeGroupFromLayout(layout, groupId).layout);
      },
    }),
    [
      applyLayout,
      layout,
      draftGroups,
      queries,
      page?._id,
      dispatch,
      setCachedLayout,
      queryClient,
      savedIds,
      savedFields,
      showsDiscountCode,
      hasDiscountCode,
      discountCodeDisabled,
      updatePage,
      layoutKey,
      pageForm,
    ],
  );

  const groups = useMemo(
    () =>
      layout
        .filter(item => item.type === 'group')
        .map(group => ({ id: group.id, name: groupDisplayName(layout, group.id) })),
    [layout],
  );

  const value = useMemo(
    () => ({ layout, displayLayout, items, toItems, groups, fieldsById, ...actions }),
    [layout, displayLayout, items, toItems, groups, fieldsById, actions],
  );

  return <FormLayoutContext.Provider value={value}>{children}</FormLayoutContext.Provider>;
}

export const useFormLayout = () => useContext(FormLayoutContext);
